import { createContext, useContext, useState, type ReactNode } from 'react'
import { useForm, FormProvider, type DefaultValues, type FieldValues, type UseFormReturn, type Resolver } from 'react-hook-form'

interface AppFormContextValue {
  isSubmitting: boolean
  apiError: string | null
  setApiError: (error: string | null) => void
}

const AppFormContext = createContext<AppFormContextValue>({
  isSubmitting: false,
  apiError: null,
  setApiError: () => {},
})

export function useAppFormContext() {
  return useContext(AppFormContext)
}

interface AppFormProps<T extends FieldValues> {
  resolver: Resolver<T>
  onSubmit: (data: T, form: UseFormReturn<T>) => void | Promise<void>
  defaultValues?: DefaultValues<T>
  children: ReactNode
}

export function AppForm<T extends FieldValues>({
  resolver,
  onSubmit,
  defaultValues,
  children,
}: AppFormProps<T>) {
  const [apiError, setApiError] = useState<string | null>(null)

  const form = useForm<T>({
    resolver,
    defaultValues,
  })

  const handleSubmit = form.handleSubmit(async (data) => {
    setApiError(null)
    try {
      await onSubmit(data, form)
    } catch {
      setApiError('Error de conexion')
    }
  })

  return (
    <AppFormContext.Provider
      value={{
        isSubmitting: form.formState.isSubmitting,
        apiError,
        setApiError,
      }}
    >
      <FormProvider {...form}>
        <form onSubmit={handleSubmit}>{children}</form>
      </FormProvider>
    </AppFormContext.Provider>
  )
}
